/**
 * NA-283: the one type-to-confirm delete dialog, shared by the Lessons and
 * Courses lists and both editor top bars.
 *
 * Replaces the hand-rolled copies that predated ModalShell (which lacked a
 * focus trap and Escape handling). Built on ModalShell, so keyboard modality —
 * trap, Escape, focus restore — comes from the shell rather than being
 * reimplemented per surface.
 *
 * Stays mounted while closed (ModalShell's contract) so focus returns to the
 * control that opened it. The confirm input deliberately carries no autoFocus:
 * the shell already focuses the first focusable child, and an autoFocus fires
 * during commit — before the shell captures the previously focused element —
 * which would make it restore focus to the input it is about to unmount.
 */

import { useEffect, useId, useMemo, useState } from 'react';
import type { CSSProperties } from 'react';
import { useTheme } from '@course-authoring/shared-types';
import { adminButton, adminField, adminText, adminTokens } from '../admin-ui';
import { ModalShell, type ModalPresentation } from './ModalShell';

const CONFIRM_WORD = 'delete';

export interface DeleteConfirmModalProps {
  isOpen: boolean;
  /** Dialog heading, e.g. "Delete Lesson?". */
  title: string;
  /** Name of the thing being deleted, shown in the warning sentence. */
  itemName: string;
  isDeleting?: boolean;
  /**
   * Refusal to show inside the dialog. The lists close and show a banner
   * instead (their row flips to disabled); surfaces with no list behind them
   * report the reason here.
   */
  errorMessage?: string | null;
  /**
   * False once the server has refused on eligibility grounds — retrying cannot
   * succeed, so the destructive control stays locked and the author's only move
   * is to close. Transient failures (a dropped connection) leave this true.
   */
  allowRetry?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  /** `admin` from the management lists; the editor top bars keep `default`. */
  presentation?: ModalPresentation;
}

export function DeleteConfirmModal({
  isOpen,
  title,
  itemName,
  isDeleting = false,
  errorMessage = null,
  allowRetry = true,
  onConfirm,
  onCancel,
  presentation = 'default',
}: DeleteConfirmModalProps) {
  const { tokens } = useTheme();
  const styles = useMemo(
    () => (presentation === 'admin' ? adminStyles : createStyles(tokens)),
    [tokens, presentation]
  );
  const titleId = useId();
  const inputId = useId();
  const [confirmText, setConfirmText] = useState('');

  // Each opening starts from an empty field: a stale match would let a second
  // delete through on a single keystroke-free confirm click.
  useEffect(() => {
    if (isOpen) setConfirmText('');
  }, [isOpen]);

  const confirmed = confirmText.trim().toLowerCase() === CONFIRM_WORD && allowRetry;

  return (
    <ModalShell
      isOpen={isOpen}
      onClose={onCancel}
      ariaLabelledBy={titleId}
      width="440px"
      presentation={presentation}
    >
      <div style={styles.body}>
        <h3 id={titleId} style={styles.title}>{title}</h3>
        <p style={styles.text}>
          Are you sure you want to delete "{itemName}"? This action cannot be undone.
        </p>
        <label htmlFor={inputId} style={styles.text}>
          Type <strong>delete</strong> to confirm:
        </label>
        <input
          id={inputId}
          type="text"
          value={confirmText}
          onChange={(event) => setConfirmText(event.target.value)}
          placeholder="Type 'delete' to confirm"
          style={styles.input}
          disabled={isDeleting}
        />
        {errorMessage && (
          <p role="alert" style={styles.error}>{errorMessage}</p>
        )}
        <div style={styles.actions}>
          <button
            type="button"
            onClick={onCancel}
            style={{ ...styles.cancelButton, ...(isDeleting ? styles.buttonDisabled : {}) }}
            disabled={isDeleting}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            style={{
              ...styles.confirmButton,
              ...(!confirmed || isDeleting ? styles.buttonDisabled : {}),
            }}
            disabled={!confirmed || isDeleting}
            aria-busy={isDeleting}
          >
            {isDeleting ? 'Deleting...' : 'Delete'}
          </button>
        </div>
      </div>
    </ModalShell>
  );
}

function createStyles(tokens: ReturnType<typeof useTheme>['tokens']): Record<string, CSSProperties> {
  return {
    body: {
      padding: '24px',
      display: 'flex',
      flexDirection: 'column',
    },
    title: {
      margin: '0 0 12px 0',
      fontSize: '18px',
      fontWeight: 600,
      color: tokens.text.primary,
    },
    text: {
      margin: '0 0 12px 0',
      fontSize: '14px',
      color: tokens.text.muted,
      lineHeight: 1.5,
    },
    input: {
      width: '100%',
      padding: '10px 12px',
      fontSize: '14px',
      border: `1px solid ${tokens.borders.default}`,
      borderRadius: '6px',
      marginBottom: '20px',
      backgroundColor: 'transparent',
      color: tokens.text.primary,
      outline: 'none',
      boxSizing: 'border-box',
    },
    error: {
      margin: '0 0 16px 0',
      fontSize: '14px',
      color: tokens.ui.errorText,
      lineHeight: 1.5,
    },
    actions: {
      display: 'flex',
      justifyContent: 'flex-end',
      gap: '12px',
    },
    cancelButton: {
      padding: '8px 16px',
      fontSize: '14px',
      color: tokens.text.primary,
      backgroundColor: 'transparent',
      border: `1px solid ${tokens.borders.default}`,
      borderRadius: '6px',
      cursor: 'pointer',
    },
    confirmButton: {
      padding: '8px 16px',
      fontSize: '14px',
      color: '#fff',
      backgroundColor: tokens.ui.errorText,
      border: 'none',
      borderRadius: '6px',
      cursor: 'pointer',
    },
    buttonDisabled: {
      opacity: 0.5,
      cursor: 'not-allowed',
    },
  };
}

const adminStyles: Record<string, CSSProperties> = {
  body: {
    padding: adminTokens.spacing.xl,
    display: 'flex',
    flexDirection: 'column',
  },
  title: {
    ...adminText.sectionTitle,
    margin: `0 0 ${adminTokens.spacing.md} 0`,
  },
  text: {
    margin: `0 0 ${adminTokens.spacing.md} 0`,
    fontSize: adminTokens.typography.body.size,
    color: adminTokens.text.secondary,
    lineHeight: adminTokens.typography.body.lineHeight,
  },
  input: {
    ...adminField.input,
    width: '100%',
    marginBottom: adminTokens.spacing.lg,
  },
  error: {
    margin: `0 0 ${adminTokens.spacing.lg} 0`,
    fontSize: adminTokens.typography.body.size,
    color: adminTokens.status.error.text,
    lineHeight: adminTokens.typography.body.lineHeight,
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    flexWrap: 'wrap',
    gap: adminTokens.spacing.md,
  },
  cancelButton: adminButton.secondary,
  confirmButton: adminButton.danger,
  buttonDisabled: adminButton.disabled,
};
